import { useState, useEffect, useCallback, useRef } from 'react';
import type { BodyEndData, ScriptLink } from './types';
import { INITIAL_BODY_END_DATA } from './types';

export function useBodyEndForm(isOpen: boolean, initialData?: Partial<BodyEndData>) {
  const [formData, setFormData] = useState<BodyEndData>({ ...INITIAL_BODY_END_DATA });

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      hasInitialized.current = true;
      setFormData({
        inlineScripts: initialData?.inlineScripts || '',
        scriptLinks: initialData?.scriptLinks || [],
        chatWidgets: initialData?.chatWidgets || [],
        noScriptContent: initialData?.noScriptContent || '',
      });
    }
    if (!isOpen) {
      hasInitialized.current = false;
    }
  }, [isOpen]); // Remove initialData from deps

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
    },
    []
  );

  // Script Links Management
  const handleAddScriptLink = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      scriptLinks: [...prev.scriptLinks, { src: '', async: true, defer: false }],
    }));
  }, []);

  const handleScriptLinkChange = useCallback(
    (index: number, field: keyof ScriptLink | string, value: any) => {
      setFormData(prev => {
        const newScriptLinks = [...prev.scriptLinks];
        newScriptLinks[index] = { ...newScriptLinks[index], [field]: value };
        return { ...prev, scriptLinks: newScriptLinks };
      });
    },
    []
  );

  const handleRemoveScriptLink = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      scriptLinks: prev.scriptLinks.filter((_, i) => i !== index),
    }));
  }, []);

  // Chat Widgets Management
  const handleAddChatWidget = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      chatWidgets: [...prev.chatWidgets, { provider: 'custom', config: {} }],
    }));
  }, []);

  const handleChatWidgetChange = useCallback((index: number, field: string, value: any) => {
    setFormData(prev => {
      const newChatWidgets = [...prev.chatWidgets];

      if (field === 'provider') {
        newChatWidgets[index] = { provider: value, config: {} };
      } else if (field.startsWith('config.')) {
        const configField = field.replace('config.', '');
        newChatWidgets[index] = {
          ...newChatWidgets[index],
          config: { ...newChatWidgets[index].config, [configField]: value },
        };
      } else {
        newChatWidgets[index] = { ...newChatWidgets[index], [field]: value };
      }

      return { ...prev, chatWidgets: newChatWidgets };
    });
  }, []);

  const handleRemoveChatWidget = useCallback((index: number) => {
    setFormData(prev => ({
      ...prev,
      chatWidgets: prev.chatWidgets.filter((_, i) => i !== index),
    }));
  }, []);

  return {
    formData,
    handleInputChange,
    handleAddScriptLink,
    handleScriptLinkChange,
    handleRemoveScriptLink,
    handleAddChatWidget,
    handleChatWidgetChange,
    handleRemoveChatWidget,
  };
}
